import React, { useEffect, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import { Panel, LiveTag, StatBox } from "../components/common/Panel";
import { CopyBtn } from "../components/common/Detail";
import { getNodes, getChainInfo, getFeeHistory } from "../lib/api";
import { fmtNum, shorten, CHAIN } from "../lib/format";

function FeeTip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  return (
    <div className="px-2.5 py-1.5 rounded-lg text-[11px] mono" style={{ background: "var(--bg-1)", border: "1px solid var(--border-2)" }}>
      <div style={{ color: "var(--amber)" }}>#{p.block}</div>
      <div style={{ color: "var(--muted)" }}>{p.feeBcc.toFixed(8)} {CHAIN.symbol}</div>
    </div>
  );
}

export default function Network() {
  const [nodes, setNodes] = useState([]);
  const [info, setInfo] = useState(null);
  const [added, setAdded] = useState("");
  const [feeHist, setFeeHist] = useState(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const [n, i, f] = await Promise.all([getNodes(), getChainInfo(), getFeeHistory(60).catch(() => null)]);
        if (!alive) return;
        setNodes(n.nodes); setInfo(i); if (f) setFeeHist(f);
      } catch {}
    };
    load();
    const id = setInterval(load, 5000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const addToWallet = async () => {
    if (!window.ethereum) { setAdded("No wallet detected. Install MetaMask."); return; }
    try {
      await window.ethereum.request({
        method: "wallet_addEthereumChain",
        params: [{
          chainId: CHAIN.chainIdHex,
          chainName: CHAIN.name,
          nativeCurrency: { name: CHAIN.symbol, symbol: CHAIN.symbol, decimals: 18 },
          rpcUrls: [CHAIN.rpcUrl],
          blockExplorerUrls: [window.location.origin],
        }],
      });
      setAdded("Blokista Chain added to your wallet!");
    } catch (e) {
      setAdded("Could not add network: " + (e.message || "rejected"));
    }
  };

  return (
    <div className="fade-in" data-testid="network-page">
      <div className="mb-4">
        <span className="eyebrow" style={{ color: "var(--amber)" }}>Infrastructure</span>
        <h1 className="font-display text-[24px] font-extrabold mt-1">Network</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Panel title="Node Health" action={<LiveTag />}>
          <div className="p-2">
            {nodes.map((n) => (
              <div key={n.id} className="flex items-center justify-between px-3 py-3.5" style={{ borderBottom: "1px solid rgba(30,40,49,0.5)" }} data-testid={`network-node-${n.id}`}>
                <div className="flex items-center gap-3">
                  <span className={`dot ${n.online ? "dot-green live-dot" : "dot-red"}`} />
                  <div>
                    <div className="font-display text-[13px] font-semibold">{n.name}</div>
                    <div className="mono text-[11px]" style={{ color: "var(--muted-2)" }}>{n.rpc}</div>
                  </div>
                </div>
                <div className="flex items-center gap-5 mono text-[12px]" style={{ color: "var(--muted)" }}>
                  <div className="text-right"><div style={{ color: "var(--text)" }}>#{fmtNum(n.blockNumber)}</div><div className="text-[10px]">height</div></div>
                  <div className="text-right"><div style={{ color: "var(--text)" }}>{n.peers ?? 0}</div><div className="text-[10px]">peers</div></div>
                  <span className={`pill ${n.online ? "pill-green" : "pill-red"}`}>{n.online ? "Online" : "Down"}</span>
                </div>
              </div>
            ))}
            {nodes.length === 0 && <div className="text-center py-8" style={{ color: "var(--muted)" }}>Loading…</div>}
          </div>
        </Panel>

        <div className="flex flex-col gap-5">
          <Panel title="Chain Parameters">
            <div className="p-5 space-y-3.5">
              {[
                ["Network Name", CHAIN.name],
                ["Client", info?.client || "Hyperledger Besu"],
                ["Consensus", info?.consensus || "QBFT"],
                ["Chain ID", `${CHAIN.chainId}  (${CHAIN.chainIdHex})`],
                ["Currency Symbol", CHAIN.symbol],
                ["Total Supply", `${fmtNum(info?.totalSupply ?? 22500000)} ${CHAIN.symbol}`],
                ["BCC VM Version", info?.hardfork || "Cancun"],
                ["Block Time", `${info?.blockTime ?? 3}s`],
                ["Network Fee", `${info?.gasPriceGwei ?? 1} Gwei (min gas price)`],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="eyebrow">{k}</span>
                  <span className="mono text-[13px]" style={{ color: "var(--text)" }}>{v}</span>
                </div>
              ))}
            </div>
          </Panel>

          <Panel title="Connect Wallet">
            <div className="p-5">
              <div className="eyebrow mb-1.5">RPC Endpoint</div>
              <div className="flex items-center gap-2 mb-4">
                <code className="mono text-[12px] flex-1 break-all px-3 py-2 rounded-lg" style={{ background: "var(--bg-2)", border: "1px solid var(--border)" }}>{CHAIN.rpcUrl}</code>
                <CopyBtn text={CHAIN.rpcUrl} testId="copy-rpc" />
              </div>
              <button onClick={addToWallet} className="btn-amber w-full" data-testid="add-to-wallet-btn">
                <i className="fa-solid fa-wallet mr-2" /> Add Blokista Chain to MetaMask
              </button>
              {added && <div className="mt-3 text-[12px] text-center" style={{ color: added.includes("added") ? "var(--green)" : "var(--red)" }} data-testid="add-wallet-status">{added}</div>}
            </div>
          </Panel>
        </div>
      </div>

      {/* Fee Economics */}
      <div className="mt-5" data-testid="fee-economics">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-[16px] font-bold">Fee Economics</h2>
          <LiveTag />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          <StatBox testId="fee-eco-current" label="Network Fee" value={`${feeHist?.currentFeeGwei ?? info?.gasPriceGwei ?? 1} Gwei`} sub="min gas price" icon="fa-solid fa-gas-pump" />
          <StatBox testId="fee-eco-collected" label="Fees Collected" value={feeHist ? `${feeHist.totalFeeBcc.toFixed(6)}` : "—"} sub={`${CHAIN.symbol} · last ${feeHist?.perBlock?.length ?? 60} blks`} icon="fa-solid fa-sack-dollar" accent="var(--green)" />
          <StatBox testId="fee-eco-validators" label="Earning Validators" value={feeHist?.perValidator?.length ?? "—"} sub="proposers paid" icon="fa-solid fa-shield-halved" accent="var(--blue)" />
          <StatBox testId="fee-eco-changes" label="Fee Changes" value={feeHist?.feeChanges?.length ?? "—"} sub="admin updates" icon="fa-solid fa-sliders" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Panel title="Fees per Block" subtitle={`${CHAIN.symbol}`} testId="panel-fee-chart">
            <div className="p-4" style={{ height: 220 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={feeHist?.perBlock || []}>
                  <defs>
                    <linearGradient id="feeG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="var(--amber)" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="var(--amber)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Tooltip content={<FeeTip />} />
                  <Area type="monotone" dataKey="feeBcc" stroke="var(--amber)" strokeWidth={1.8} fill="url(#feeG)" isAnimationActive animationDuration={500} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          <Panel title="Validator Fee Earnings" subtitle="recent window" bodyClass="overflow-auto" testId="panel-validator-earnings">
            <div className="p-4 space-y-3">
              {(feeHist?.perValidator || []).map((v, i) => {
                const max = feeHist.perValidator[0]?.feeBcc || 1;
                const pct = max > 0 ? (v.feeBcc / max) * 100 : 0;
                return (
                  <div key={v.validator} data-testid={`validator-earning-${i}`}>
                    <div className="flex items-center justify-between mb-1.5">
                      <a href={`/address/${v.validator}`} className="link-amber mono text-[12px]">{shorten(v.validator, 8, 6)}</a>
                      <span className="mono text-[12px]" style={{ color: "var(--amber)" }}>{v.feeBcc.toFixed(8)} {CHAIN.symbol}</span>
                    </div>
                    <div className="rounded-full overflow-hidden" style={{ height: 8, background: "var(--bg-2)" }}>
                      <div style={{ width: `${pct}%`, height: "100%", background: "linear-gradient(90deg, var(--amber), #f0a500)", transition: "width 0.5s" }} />
                    </div>
                    <div className="text-[10.5px] mt-1 mono" style={{ color: "var(--muted-2)" }}>{v.blocks} blocks proposed</div>
                  </div>
                );
              })}
              {(!feeHist || feeHist.perValidator.length === 0) && <div className="text-center py-8" style={{ color: "var(--muted)" }}>Collecting fee data…</div>}
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
}
